import { ACCOUNT_FEATURE, ROLE_LIMIT, selectAvailableUserRoles } from '@socialive/common';
import { useFeature } from '@socialive/features';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectRolesLicensingData } from '~/store';
import { userShape } from '../../helpers/propTypeShapes';
import UserRoleSelector from '../UserRoleSelector';

export default function UserTableRowRenderer({
  item,
  handleSelectItem,
  enableAdminOverride,
  handleItemClicked,
  currentUserId,
  handleEditRole,
}) {
  const availableRoles = useSelector(selectAvailableUserRoles);
  const rolesLicense = useSelector(selectRolesLicensingData);
  const [, { enabled: isUserRolesEnabled }] = useFeature(ACCOUNT_FEATURE.USER_ROLES);

  const { id: primaryKey, selected: isSelected, user, role } = item;
  const { id, name, email, location } = user;

  const [timestamp, setTimestamp] = useState(Date.now());

  const displayRole = role.toLowerCase();
  const added = moment(user.createdAt).format('MMM DD, YYYY h:mma');

  const onCheckBoxChange = useCallback(() => {
    handleSelectItem(primaryKey, !isSelected);
  }, [handleSelectItem, primaryKey, isSelected]);

  const onRoleSelected = useCallback(
    ({ target: { value: newRole } }) => {
      const targetRole = rolesLicense?.[newRole];
      if (
        isUserRolesEnabled &&
        targetRole?.limit !== ROLE_LIMIT.UNLIMITED &&
        targetRole?.current >= targetRole?.limit
      ) {
        toast.error(`You have reached the license limit for the ${newRole} role`);
        setTimestamp(Date.now());
        return;
      }

      handleEditRole(primaryKey, newRole);
    },
    [handleEditRole, primaryKey, isUserRolesEnabled, rolesLicense]
  );

  return (
    <tr key={`user_${id}`} className={`body-row ${isSelected ? 'selected' : ''}`} data-user-name={name}>
      <td className="user-select">
        {handleSelectItem && (
          <input className="user-checkbox" type="checkbox" checked={isSelected} onChange={onCheckBoxChange} />
        )}
      </td>
      <td className="body-row-name">
        {enableAdminOverride && handleItemClicked ? (
          <button type="button" className="body-row-name-link btn text" onClick={() => handleItemClicked(id)}>
            {name}
          </button>
        ) : (
          <span className="body-row-name">{name}</span>
        )}
      </td>
      <td className="user-email">{email}</td>
      <td className="user-location">{location}</td>
      <td className="user-role">
        {enableAdminOverride && handleEditRole && currentUserId !== id ? (
          <div className="role-selector-wrapper">
            <UserRoleSelector
              // This timestamp is needed to force a re-render to override the inherent behavior of a select html element
              // selecting the onchange option, ignoring the actual state value
              key={timestamp}
              roleList={availableRoles}
              onRoleChanged={onRoleSelected}
              role={role}
              className="role-selector"
            />
          </div>
        ) : (
          <div className="flat-role">{displayRole}</div>
        )}
      </td>
      <td className="user-created-date">{added}</td>
    </tr>
  );
}

UserTableRowRenderer.defaultProps = {
  currentUserId: null,
  enableAdminOverride: false,
  handleEditRole: null,
  handleItemClicked: null,
  handleSelectItem: null,
};

UserTableRowRenderer.propTypes = {
  currentUserId: PropTypes.number,
  enableAdminOverride: PropTypes.bool,
  handleSelectItem: PropTypes.func,
  handleItemClicked: PropTypes.func,
  handleEditRole: PropTypes.func,
  item: PropTypes.shape({
    id: PropTypes.number,
    selected: PropTypes.bool,
    user: userShape,
    role: PropTypes.string,
  }).isRequired,
};
